import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Avatar,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Link,
  Divider,
  Switch,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { styled } from "@mui/system";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import heic2any from "heic2any";
import { useAuth } from "../../contexts/AuthContext";
import {
  getUserProfile,
  saveUserProfile,
  uploadProfileImage,
  uploadSponsorLogo,
} from "../../firebase";
import { UserProfile } from "../../types";

const ProfileCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  overflow: "visible",
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  border: "4px solid white",
  boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
  cursor: "pointer",
  marginTop: -60,
  marginLeft: theme.spacing(3),
  marginBottom: theme.spacing(2),
  backgroundColor: "#3b82f6",
  "&:hover": {
    opacity: 0.9,
  },
}));

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const LabelValue = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ mb: 2 }}>
    <p>{label}</p>
    <p>{value || "Not provided"}</p>
  </Box>
);

const Profile: React.FC = () => {
  const { currentUser } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sponsorLogoInputRef = useRef<HTMLInputElement>(null);

  const [profile, setProfile] = useState<Partial<UserProfile> | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);

  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [socialMediaLink, setSocialMediaLink] = useState("");
  const [sport, setSport] = useState("");
  const [bio, setBio] = useState("");
  const [location, setLocation] = useState("");
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // Sponsorship fields
  const [sponsorName, setSponsorName] = useState("");
  const [sponsorLogo, setSponsorLogo] = useState<File | null>(null);
  const [sponsorLogoPreviewUrl, setSponsorLogoPreviewUrl] = useState<
    string | null
  >(null);
  const [openToSponsorship, setOpenToSponsorship] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        const userProfile = await getUserProfile(currentUser.uid);
        setProfile(userProfile);

        // Initialize form fields
        if (userProfile) {
          setDisplayName(userProfile.displayName || "");
          setDescription(userProfile.description || "");
          setSocialMediaLink(userProfile.socialMediaLink || "");
          setSport(userProfile.sport || "");
          setBio(userProfile.bio || "");
          setLocation(userProfile.location || "");
          setPreviewUrl(userProfile.photoURL || null);
          // Initialize sponsorship fields
          setSponsorName(userProfile.sponsorName || "");
          setSponsorLogoPreviewUrl(userProfile.sponsorLogoURL || null);
          setOpenToSponsorship(userProfile.openToSponsorship || false);
        }
      } catch (err: any) {
        console.error("Error fetching profile:", err);
        setError("Failed to load profile information");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [currentUser]);

  const handleEditClick = () => {
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);

    // Reset form fields to profile values
    if (profile) {
      setDisplayName(profile.displayName || "");
      setDescription(profile.description || "");
      setSocialMediaLink(profile.socialMediaLink || "");
      setSport(profile.sport || "");
      setBio(profile.bio || "");
      setLocation(profile.location || "");
      setPreviewUrl(profile.photoURL || null);
      // Reset sponsorship fields
      setSponsorName(profile.sponsorName || "");
      setSponsorLogoPreviewUrl(profile.sponsorLogoURL || null);
      setOpenToSponsorship(profile.openToSponsorship || false);
    }

    setProfileImage(null);
    setSponsorLogo(null);
  };

  const handleAvatarClick = () => {
    if (editMode) {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];

      try {
        let processedFile = file;

        // Check if the file is HEIC format and convert to JPEG
        if (
          file.type === "image/heic" ||
          file.type === "image/heif" ||
          file.name.toLowerCase().endsWith(".heic") ||
          file.name.toLowerCase().endsWith(".heif")
        ) {
          setError(null); // Clear any previous errors

          // Convert HEIC to JPEG
          const convertedBlob = await heic2any({
            blob: file,
            toType: "image/jpeg",
            quality: 0.8,
          });

          // Create a new File object from the converted blob
          const convertedFile = new File(
            [convertedBlob as Blob],
            file.name.replace(/\.(heic|heif)$/i, ".jpg"),
            { type: "image/jpeg" }
          );

          processedFile = convertedFile;
        }

        setProfileImage(processedFile);

        // Create a preview URL for the selected/converted image
        const reader = new FileReader();
        reader.onload = () => {
          setPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(processedFile);
      } catch (error) {
        console.error("Error processing image:", error);
        setError("Failed to process the image. Please try a different file.");
      }
    }
  };

  const handleSponsorLogoClick = () => {
    if (editMode) {
      sponsorLogoInputRef.current?.click();
    }
  };

  const handleSponsorLogoChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];

      try {
        let processedFile = file;

        // Check if the file is HEIC format and convert to JPEG
        if (
          file.type === "image/heic" ||
          file.type === "image/heif" ||
          file.name.toLowerCase().endsWith(".heic") ||
          file.name.toLowerCase().endsWith(".heif")
        ) {
          setError(null); // Clear any previous errors

          // Convert HEIC to JPEG
          const convertedBlob = await heic2any({
            blob: file,
            toType: "image/jpeg",
            quality: 0.8,
          });

          // Create a new File object from the converted blob
          const convertedFile = new File(
            [convertedBlob as Blob],
            file.name.replace(/\.(heic|heif)$/i, ".jpg"),
            { type: "image/jpeg" }
          );

          processedFile = convertedFile;
        }

        setSponsorLogo(processedFile);

        // Create a preview URL for the selected/converted image
        const reader = new FileReader();
        reader.onload = () => {
          setSponsorLogoPreviewUrl(reader.result as string);
        };
        reader.readAsDataURL(processedFile);
      } catch (error) {
        console.error("Error processing sponsor logo:", error);
        setError(
          "Failed to process the sponsor logo. Please try a different file."
        );
      }
    }
  };

  const handleSaveProfile = async () => {
    if (!currentUser) return;

    setSaving(true);
    setError(null);

    try {
      let photoURL = profile?.photoURL || "";
      let sponsorLogoURL = profile?.sponsorLogoURL || "";

      // Upload new profile image if one was selected
      if (profileImage) {
        photoURL = await uploadProfileImage(currentUser.uid, profileImage);
      }

      // Upload new sponsor logo if one was selected
      if (sponsorLogo) {
        sponsorLogoURL = await uploadSponsorLogo(currentUser.uid, sponsorLogo);
      }

      // Create user profile object
      const updatedProfile: Partial<UserProfile> = {
        displayName: displayName.trim(),
        description: description.trim(),
        socialMediaLink: socialMediaLink.trim(),
        sport: sport.trim(),
        bio: bio.trim(),
        location: location.trim(),
        photoURL,
        // Sponsorship fields
        sponsorName: sponsorName.trim(),
        sponsorLogoURL,
        openToSponsorship,
        updatedAt: Date.now(),
      };

      // Save user profile to Firestore
      await saveUserProfile(currentUser.uid, updatedProfile);

      // Update local profile state
      setProfile({ ...profile, ...updatedProfile });

      // Exit edit mode
      setEditMode(false);
    } catch (err: any) {
      console.error("Error saving profile:", err);
      setError(err.message || "Failed to save profile changes");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* <p className="text-3xl font-bold mb-2">My Profile</p>
      <p className="text-gold font-josefin text-md">
        Update your public Wattlenest Profile here.
      </p> */}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ position: "relative", mb: 8 }}>
        {/* Profile Header */}
        <Box
          sx={{
            height: 50,
            borderRadius: "8px 8px 0 0",
          }}
        />

        <div>
          <div className="flex justify-between items-center">
            <StyledAvatar
              src={previewUrl || undefined}
              alt={profile?.displayName || "User"}
              onClick={handleAvatarClick}
            >
              {!previewUrl &&
                (editMode ? (
                  <CloudUploadIcon />
                ) : (
                  profile?.displayName?.charAt(0) ||
                  currentUser?.email?.charAt(0) ||
                  "U"
                ))}
            </StyledAvatar>

            {editMode ? (
              <div className="p-4">
                <button
                  onClick={handleCancelEdit}
                  disabled={saving}
                  className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold ${
                    saving ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={handleEditClick}
                className="inline-flex items-center px-4 py-2 m-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg
                  className="-ml-1 mr-2 h-5 w-5 text-gray-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                Edit Profile
              </button>
            )}
          </div>

          {editMode ? (
            <>
              <VisuallyHiddenInput
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleFileChange}
              />
              <VisuallyHiddenInput
                type="file"
                accept="image/*"
                ref={sponsorLogoInputRef}
                onChange={handleSponsorLogoChange}
              />
            </>
          ) : null}

          <CardContent>
            {editMode ? (
              <div>
                <TextField
                  label="Name"
                  fullWidth
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  margin="normal"
                  required
                />
                <TextField
                  label="Bio"
                  fullWidth
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  margin="normal"
                  multiline
                  rows={1}
                  helperText="Similar to IG bio"
                />
                <TextField
                  label="My Story"
                  fullWidth
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  margin="normal"
                  multiline
                  rows={4}
                  helperText="Sporting journey, achievements & goals"
                />
                <TextField
                  label="Sport"
                  fullWidth
                  value={sport}
                  onChange={(e) => setSport(e.target.value)}
                  margin="normal"
                />
                <TextField
                  label="Location"
                  fullWidth
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  margin="normal"
                />

                <TextField
                  label="Link, where would you like to direct potential sponsors to?"
                  fullWidth
                  value={socialMediaLink}
                  onChange={(e) => setSocialMediaLink(e.target.value)}
                  margin="normal"
                  placeholder="e.g. https://twitter.com/yourusername"
                />

                {/* Sponsorship Section */}
                <Box sx={{ mt: 4, mb: 2 }}>
                  <Typography variant="h6" component="h3">
                    Sponsorship Information
                  </Typography>
                </Box>

                <TextField
                  label="Sponsor Name"
                  fullWidth
                  value={sponsorName}
                  onChange={(e) => setSponsorName(e.target.value)}
                  margin="normal"
                  placeholder="Your current sponsor (if any)"
                />

                {/* Sponsor Logo Upload */}
                <Box
                  sx={{
                    mt: 3,
                    mb: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Box
                    onClick={handleSponsorLogoClick}
                    sx={{
                      width: 120,
                      height: 120,
                      border: "1px dashed #ccc",
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      overflow: "hidden",
                      mb: 1,
                    }}
                  >
                    {sponsorLogoPreviewUrl ? (
                      <img
                        src={sponsorLogoPreviewUrl}
                        alt="Sponsor Logo"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <CloudUploadIcon fontSize="large" color="action" />
                    )}
                  </Box>

                  <Typography
                    variant="body2"
                    className="text-gray-600 text-center"
                  >
                    Upload sponsor logo (if applicable)
                  </Typography>
                </Box>

                {/* Open to Sponsorship Switch */}
                <Box sx={{ mt: 2, mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={openToSponsorship}
                        onChange={(e) => setOpenToSponsorship(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Open to sponsorship opportunities"
                  />
                </Box>

                <div className="pt-5">
                  <button
                    onClick={handleSaveProfile}
                    disabled={saving}
                    className={`inline-flex w-full items-center px-4 py-2 mr-1 mb-2 rounded-md shadow-sm text-sm font-medium text-white bg-gold hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold ${
                      saving ? "opacity-75 cursor-not-allowed" : ""
                    }`}
                  >
                    {saving ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      <>
                        <svg
                          className="-ml-1 mr-2 h-5 w-5"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                          />
                        </svg>
                        Save
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="font-bold text-gold text-xl">
                  {profile?.displayName || "Unnamed Athlete"}
                </p>
                <Divider sx={{ my: 2 }} />
                <p className="font-bold text-xs">
                  My Short Bio{" "}
                  <span className="font-light">(similar to IG bio)</span>
                </p>
                <p>{profile?.bio || ""}</p>
                <Divider sx={{ my: 2 }} />
                <p className="font-bold text-xs">
                  My Story{" "}
                  <span className="font-light">
                    (Sporting journey, achievements & goals)
                  </span>
                </p>
                <p>{profile?.description || ""}</p>
                <Divider sx={{ my: 2 }} />
                <p className="font-bold text-xs">Sport</p>
                <p>{profile?.sport || ""}</p>
                <Divider sx={{ my: 2 }} />
                <p className="font-bold text-xs">Location</p>
                <p>{profile?.location || ""}</p>
                <Divider sx={{ my: 2 }} />
                <p className="font-bold text-xs">Link</p>
                {profile?.socialMediaLink ? (
                  <Link
                    href={profile.socialMediaLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {profile.socialMediaLink}
                  </Link>
                ) : (
                  <p>Not provided</p>
                )}

                {/* Sponsorship Information in View Mode */}
                <Divider sx={{ my: 2 }} />
                <p className="font-bold text-xs">Sponsor</p>
                <p>{profile?.sponsorName || "None"}</p>

                {profile?.sponsorLogoURL && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <p className="font-bold text-xs">Sponsor Logo</p>
                    <Box sx={{ mt: 1, mb: 2, maxWidth: 150 }}>
                      <img
                        src={profile.sponsorLogoURL}
                        alt="Sponsor Logo"
                        style={{ maxWidth: "100%" }}
                      />
                    </Box>
                  </>
                )}

                <Divider sx={{ my: 2 }} />
                <p className="font-bold text-xs">Sponsorship Status</p>
                <p>
                  {profile?.openToSponsorship
                    ? "Open to sponsorship opportunities"
                    : "Not currently seeking sponsors"}
                </p>

                <Divider sx={{ my: 2 }} />
                <p className="font-bold text-xs">Member since</p>
                <p>
                  {profile?.createdAt
                    ? new Date(profile.createdAt).toLocaleDateString()
                    : ""}
                </p>
              </>
            )}
          </CardContent>
        </div>
      </Box>
    </Box>
  );
};

export default Profile;
